import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition, IViewDefinition, IAggregation } from "./DBInterfaces";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBListManager from "./DBListManager";
import DBCanvas from "./DBCanvas";
import DBDataGrid from "./DBDataGrid";
import DBStringUtils from "./DBStringUtils";

interface IDataRow {
    [key: string]: any;
}

interface IFieldItems {
    Enum: string[];
}

interface IListSettings {
    [key: string]: any;
}

interface ISchemaImportList {
    title: string;
    properties: Record<string, IFieldDefinition> | undefined;
    data: IDataRow[] | undefined;
    views: IViewDefinition[] | undefined;
    listType: string | undefined;
    listTemplate: number | undefined;
    libraryType: string | undefined;
    settings: IListSettings | undefined;
    useContentType: boolean | undefined;
    contentTypeName: string | undefined;
    contentTypeGroup: string | undefined;
}

interface ISchemaImport {
    lists: ISchemaImportList[];
}

const builtInListTitles: string[] = [
    "appdata", "appfiles", "composed looks", "content type publishing error log",
    "converted forms", "device channels", "form templates", "fpdatasources",
    "get started with your site", "list template gallery", "long running operation status",
    "maintenance log library", "master page gallery", "microfeed", "nointegratedapps",
    "project policy item list", "quick deploy items", "relationships list",
    "reusable content", "sharing links", "site assets", "site pages",
    "solution gallery", "style library", "suggested content browser locations",
    "taxonomy hidden list", "theme gallery", "translation packages",
    "translation status", "user information list", "variation labels",
    "variation tier 1 - categories", "variation tier 2 - categories",
    "web part gallery", "wfpub", "workflow history", "workflow tasks"
];

const builtInPaths: string[] = [
    "/_catalogs/", "/lists/taxonomyhiddenlist", "/lists/publishedlinks",
    "/_cts/", "/siteassets", "/sitepages", "/style library",
    "/formservertemplates", "/publishingimages"
];

const systemGroups: string[] = [
    "_Hidden", "Base Columns", "Content Feedback", "Core Contact and Calendar Columns",
    "Core Document Columns", "Core Task and Issue Columns", "Custom Columns",
    "Display Template Columns", "Document and Record Management Columns",
    "Enterprise Keywords Group", "Extended Columns", "JavaScript Display Template Columns",
    "Page Layout Columns", "Publishing Columns", "Reports", "Status Indicators",
    "Translation Columns"
];

const systemFields: string[] = [
    "ContentType", "Title", "Modified", "Created", "Author", "Editor",
    "Attachments", "DocIcon", "ItemChildCount", "FolderChildCount",
    "AppAuthor", "AppEditor", "ComplianceAssetId", "SMTotalSize",
    "SMLastModifiedDate", "SMTotalFileStreamSize", "SMTotalFileCount",
    "PermMask", "CheckedOutUserId", "IsCheckedoutToLocal",
    "CheckoutUser", "UniqueId", "SyncClientId", "ProgId",
    "ScopeId", "VirusStatus", "CheckedOutTitle", "LinkCheckedOutTitle",
    "_CheckinComment", "LinkFilenameNoMenu", "LinkFilename", "LinkFilename2",
    "BaseName", "FileSizeDisplay", "MetaInfo", "ItemType",
    "ServerUrl", "EncodedAbsUrl", "File_x0020_Type", "HTML_x0020_File_x0020_Type",
    "ParentVersionString", "ParentLeafName", "DocConcurrencyNumber",
    "Combine", "RepairDocument", "FileRef", "FileDirRef",
    "Last_x0020_Modified", "Created_x0020_Date", "FSObjType",
    "SortBehavior", "FileLeafRef", "Label", "Edit",
    "SelectTitle", "InstanceID", "Order", "GUID",
    "WorkflowVersion", "WorkflowInstanceID", "AccessPolicy",
    "OriginatorId", "NoExecute", "ContentVersion",
    "ComplianceFlags", "ComplianceTag", "ComplianceTagWrittenTime",
    "ComplianceTagUserId", "_ComplianceFlags", "_ComplianceTag",
    "_ComplianceTagWrittenTime", "_ComplianceTagUserId",
    "_IsRecord", "AccessPolicy", "SMTotalSize",
    "_UIVersion", "_UIVersionString", "_LikeCount", "_CommentCount",
    "TaxCatchAll", "TaxCatchAllLabel"
];

const systemTypes: string[] = [
    "Computed", "ContentTypeId", "Attachments", "ModStat", "WorkflowStatus"
];

const userTemplates: number[] = [
    100, 101, 102, 103, 104, 105, 106, 107, 108, 109,
    110, 113, 115, 116, 117, 118, 119, 120, 121, 122,
    123, 130, 140, 150, 160, 170, 171
];

export default class DBImportExport {

    public static ImportSchema = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const importInput: HTMLInputElement = document.getElementById("importInput") as HTMLInputElement;
            importInput.value = "";
            DBModalManager.OpenModal("importModal");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ImportSchema");
        }
    };

    public static ProcessImport = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const textarea: HTMLTextAreaElement = document.getElementById("importInput") as HTMLTextAreaElement;
            const jsonText: string = textarea.value.trim();
            if (jsonText === "") {
                DBModalManager.ShowToast("Please paste JSON schema to import", "error");
                return;
            }
            const schema: ISchemaImport = JSON.parse(jsonText) as ISchemaImport;
            if (!schema.lists || !Array.isArray(schema.lists)) {
                DBModalManager.ShowToast("Invalid schema: missing lists array", "error");
                return;
            }
            DBModalManager.ShowLoading("Importing schema...", false);
            const processTimeout = (): void => {
                DBImportExport.ExecuteSchemaImport(schema.lists);
            };
            setTimeout(processTimeout, 100);
        } catch (e) {
            DBModalManager.HideLoading();
            DBModalManager.ShowToast("Invalid JSON format", "error");
            ks.kGlobalErrorHandler(e, "DBImportExport.ProcessImport");
        }
    };

    private static ExecuteSchemaImport = (lists: ISchemaImportList[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const mapListItem = (list: ISchemaImportList, index: number): IListDefinition => {
                return DBImportExport.MapSchemaListToDefinition(list, index);
            };
            const newLists: IListDefinition[] = lists.map(mapListItem);
            const addListToState = (list: IListDefinition): void => {
                DBState.Lists.push(list);
            };
            newLists.forEach(addListToState);
            DBState.SaveState();
            DBListManager.RenderLists();
            DBModalManager.HideLoading();
            DBModalManager.CloseModal("importModal");
            DBModalManager.ShowToast("Imported " + newLists.length + " list(s)", "success");
        } catch (e) {
            DBModalManager.HideLoading();
            DBModalManager.ShowToast("Error importing schema", "error");
            ks.kGlobalErrorHandler(e, "DBImportExport.ExecuteSchemaImport");
        }
    };

    private static MapSchemaListToDefinition = (list: ISchemaImportList, index: number): IListDefinition => {
        if (ks.kErrorMode === true) { return {} as IListDefinition; }
        try {
            DBState.FieldIdCounter++;
            const listDef: IListDefinition = {
                Id: "list_" + DBState.FieldIdCounter,
                Title: list.title,
                Properties: list.properties || {},
                Data: list.data || [],
                Views: list.views || [],
                ListType: list.listType || "list",
                ListTemplate: list.listTemplate || 100,
                LibraryType: list.libraryType,
                Settings: list.settings || null,
                Position: { X: 40 + index * 60, Y: 40 + index * 60 },
                Collapsed: false,
                UseContentType: list.useContentType || false,
                ContentTypeName: list.contentTypeName || "",
                ContentTypeGroup: list.contentTypeGroup || "Custom Content Types"
            } as IListDefinition;
            return listDef;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.MapSchemaListToDefinition");
            return {} as IListDefinition;
        }
    };

    public static OpenRestApiModal = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBModalManager.OpenModal("restApiModal");
            DBImportExport.ClearRestApiInputs();
            DBImportExport.SetupRestApiUrlListener();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.OpenRestApiModal");
        }
    };

    private static ClearRestApiInputs = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const siteUrlInput: HTMLInputElement = document.getElementById("restApiSiteUrl") as HTMLInputElement;
            const fullUrlArea: HTMLTextAreaElement = document.getElementById("restApiFullUrl") as HTMLTextAreaElement;
            const jsonInput: HTMLTextAreaElement = document.getElementById("restApiJsonInput") as HTMLTextAreaElement;
            const preview: HTMLElement = document.getElementById("restApiPreview") as HTMLElement;
            siteUrlInput.value = "";
            fullUrlArea.value = "";
            jsonInput.value = "";
            preview.style.display = "none";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ClearRestApiInputs");
        }
    };

    private static SetupRestApiUrlListener = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const siteUrlInput: HTMLInputElement = document.getElementById("restApiSiteUrl") as HTMLInputElement;
            const onSiteUrlInput = (): void => {
                DBImportExport.BuildRestApiUrl(siteUrlInput.value);
            };
            siteUrlInput.addEventListener("input", onSiteUrlInput);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.SetupRestApiUrlListener");
        }
    };

    private static BuildRestApiUrl = (siteUrl: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fullUrlArea: HTMLTextAreaElement = document.getElementById("restApiFullUrl") as HTMLTextAreaElement;
            let url: string = siteUrl.trim();
            if (url.endsWith("/")) {
                url = url.substring(0, url.length - 1);
            }
            if (url === "") {
                fullUrlArea.value = "";
                return;
            }
            const filter: string = "Hidden eq false and (BaseTemplate eq 100 or BaseTemplate eq 101 or " +
                "BaseTemplate eq 102 or BaseTemplate eq 103 or BaseTemplate eq 104 or " +
                "BaseTemplate eq 105 or BaseTemplate eq 106 or BaseTemplate eq 107 or " +
                "BaseTemplate eq 108 or BaseTemplate eq 109 or BaseTemplate eq 110 or " +
                "BaseTemplate eq 113 or BaseTemplate eq 115 or BaseTemplate eq 116 or " +
                "BaseTemplate eq 117 or BaseTemplate eq 118 or BaseTemplate eq 119 or " +
                "BaseTemplate eq 120 or BaseTemplate eq 121 or BaseTemplate eq 130 or " +
                "BaseTemplate eq 140 or BaseTemplate eq 150 or BaseTemplate eq 170 or " +
                "BaseTemplate eq 171)";
            const select: string = "Id,Title,Hidden,BaseTemplate,Description,SchemaXml," +
                "EnableAttachments,EnableVersioning,EnableMinorVersions,MajorVersionLimit," +
                "MajorWithMinorVersionsLimit,DraftVersionVisibility,ForceCheckout," +
                "EnableModeration,NoCrawl,DefaultDisplayFormUrl,DefaultEditFormUrl," +
                "DefaultNewFormUrl,ContentTypesEnabled";
            const expand: string = "Fields,RootFolder,Views";
            const fullUrl: string = url + "/_api/web/lists?$filter=" + filter +
                "&$select=" + select + "&$expand=" + expand;
            fullUrlArea.value = fullUrl;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.BuildRestApiUrl");
        }
    };

    public static CopyRestApiUrl = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fullUrlArea: HTMLTextAreaElement = document.getElementById("restApiFullUrl") as HTMLTextAreaElement;
            const urlValue: string = fullUrlArea.value;
            if (urlValue === "") {
                DBModalManager.ShowToast("No URL to copy", "error");
                return;
            }
            navigator.clipboard.writeText(urlValue);
            DBModalManager.ShowToast("URL copied to clipboard", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.CopyRestApiUrl");
        }
    };

    public static MapSpFieldType = (typeAsString: string): string => {
        if (ks.kErrorMode === true) { return "text"; }
        try {
            const typeMap: Record<string, string> = {
                "Text": "text",
                "Note": "note",
                "Number": "number",
                "Currency": "currency",
                "Integer": "integer",
                "Boolean": "boolean",
                "DateTime": "date",
                "User": "user",
                "UserMulti": "user",
                "Lookup": "lookup",
                "LookupMulti": "lookup",
                "Choice": "choice",
                "MultiChoice": "multichoice",
                "URL": "url",
                "Calculated": "formula",
                "Counter": "number",
                "Geolocation": "geolocation"
            };
            const mapped: string | undefined = typeMap[typeAsString];
            if (mapped !== undefined) {
                return mapped;
            }
            return "text";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.MapSpFieldType");
            return "text";
        }
    };

    public static IsUserList = (spList: any): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            if (spList.Hidden === true) {
                return false;
            }
            if (DBImportExport.IsHiddenInSchema(spList)) {
                return false;
            }
            const titleLower: string = spList.Title.toLowerCase();
            if (builtInListTitles.indexOf(titleLower) !== -1) {
                return false;
            }
            if (DBImportExport.IsBuiltInPath(spList)) {
                return false;
            }
            if (userTemplates.indexOf(spList.BaseTemplate) === -1) {
                return false;
            }
            return true;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.IsUserList");
            return false;
        }
    };

    private static IsHiddenInSchema = (spList: any): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            if (spList.SchemaXml === null || spList.SchemaXml === undefined) {
                return false;
            }
            const schemaXml: string = spList.SchemaXml;
            const listTagMatch: RegExpMatchArray | null = schemaXml.match(/<List[^>]*>/);
            if (listTagMatch === null) {
                return false;
            }
            const listTag: string = listTagMatch[0];
            return listTag.indexOf('Hidden="True"') !== -1;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.IsHiddenInSchema");
            return false;
        }
    };

    private static IsBuiltInPath = (spList: any): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            if (spList.RootFolder === null || spList.RootFolder === undefined) {
                return false;
            }
            const serverRelUrl: string = spList.RootFolder.ServerRelativeUrl;
            if (serverRelUrl === null || serverRelUrl === undefined) {
                return false;
            }
            const urlLower: string = serverRelUrl.toLowerCase();
            const checkPath = (path: string): boolean => {
                return urlLower.indexOf(path) !== -1;
            };
            const matchingPaths: string[] = builtInPaths.filter(checkPath);
            return matchingPaths.length > 0;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.IsBuiltInPath");
            return false;
        }
    };

    public static IsUserField = (spField: any): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            if (spField.CanBeDeleted === false) {
                return false;
            }
            if (spField.Group !== null && spField.Group !== undefined) {
                if (systemGroups.indexOf(spField.Group) !== -1) {
                    return false;
                }
            }
            if (systemFields.indexOf(spField.InternalName) !== -1) {
                return false;
            }
            if (systemTypes.indexOf(spField.TypeAsString) !== -1) {
                return false;
            }
            if (spField.InternalName.indexOf("_") === 0) {
                return false;
            }
            return true;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.IsUserField");
            return false;
        }
    };

    public static ImportFromRestApi = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const jsonInput: HTMLTextAreaElement = document.getElementById("restApiJsonInput") as HTMLTextAreaElement;
            const jsonText: string = jsonInput.value.trim();
            if (jsonText === "") {
                DBModalManager.ShowToast("Please paste the REST API JSON response", "error");
                return;
            }
            const data: any = JSON.parse(jsonText);
            const spLists: any[] = data.d !== undefined ? data.d.results : data.value;
            if (!spLists || !Array.isArray(spLists)) {
                DBModalManager.ShowToast("Invalid REST API response format", "error");
                return;
            }
            const filterUserList = (spList: any): boolean => {
                return DBImportExport.IsUserList(spList);
            };
            const userLists: any[] = spLists.filter(filterUserList);
            if (userLists.length === 0) {
                DBModalManager.ShowToast("No user-created lists found", "error");
                return;
            }
            DBModalManager.ShowLoading("Importing from SharePoint...", false);
            const processTimeout = (): void => {
                DBImportExport.ProcessRestApiLists(userLists, spLists);
            };
            setTimeout(processTimeout, 100);
        } catch (e) {
            DBModalManager.HideLoading();
            DBModalManager.ShowToast("Invalid JSON format", "error");
            ks.kGlobalErrorHandler(e, "DBImportExport.ImportFromRestApi");
        }
    };

    private static ProcessRestApiLists = (userLists: any[], allSpLists: any[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const newLists: IListDefinition[] = [];
            const processEachList = (spList: any, index: number): void => {
                const listDef: IListDefinition = DBImportExport.ProcessSingleSpList(spList, index);
                newLists.push(listDef);
            };
            userLists.forEach(processEachList);
            const addListToState = (list: IListDefinition): void => {
                DBState.Lists.push(list);
            };
            newLists.forEach(addListToState);
            DBImportExport.ResolveLookupReferences(newLists, allSpLists);
            DBState.SaveState();
            DBListManager.RenderLists();
            DBModalManager.HideLoading();
            DBModalManager.CloseModal("restApiModal");
            DBModalManager.ShowToast("Imported " + newLists.length + " list(s) from SharePoint", "success");
        } catch (e) {
            DBModalManager.HideLoading();
            DBModalManager.ShowToast("Error processing REST API data", "error");
            ks.kGlobalErrorHandler(e, "DBImportExport.ProcessRestApiLists");
        }
    };

    private static ProcessSingleSpList = (spList: any, index: number): IListDefinition => {
        if (ks.kErrorMode === true) { return {} as IListDefinition; }
        try {
            DBState.FieldIdCounter++;
            const properties: Record<string, IFieldDefinition> = DBImportExport.ConvertSpFields(spList);
            const views: IViewDefinition[] = DBImportExport.ConvertSpViews(spList);
            const settings: IListSettings | null = DBImportExport.ParseListSettings(spList);
            const listType: string = spList.BaseTemplate === 101 ? "library" : "list";
            const listDef: IListDefinition = {
                Id: "list_" + DBState.FieldIdCounter,
                Title: spList.Title,
                Properties: properties,
                Data: [],
                Views: views,
                ListType: listType,
                ListTemplate: spList.BaseTemplate,
                LibraryType: spList.BaseTemplate === 101 ? "documentLibrary" : undefined,
                Settings: settings,
                Position: { X: 40 + index * 60, Y: 40 + index * 60 },
                Collapsed: false,
                UseContentType: spList.ContentTypesEnabled || false,
                ContentTypeName: "",
                ContentTypeGroup: "Custom Content Types"
            } as IListDefinition;
            return listDef;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ProcessSingleSpList");
            return {} as IListDefinition;
        }
    };

    private static ConvertSpFields = (spList: any): Record<string, IFieldDefinition> => {
        if (ks.kErrorMode === true) { return {}; }
        try {
            const properties: Record<string, IFieldDefinition> = {};
            if (spList.Fields === null || spList.Fields === undefined) {
                return properties;
            }
            const fields: any[] = spList.Fields.results || spList.Fields;
            if (!Array.isArray(fields)) {
                return properties;
            }
            const processField = (spField: any): void => {
                if (DBImportExport.IsUserField(spField)) {
                    const fieldDef: IFieldDefinition = DBImportExport.ConvertSingleSpField(spField);
                    properties[spField.InternalName] = fieldDef;
                }
            };
            fields.forEach(processField);
            return properties;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ConvertSpFields");
            return {};
        }
    };

    private static ConvertSingleSpField = (spField: any): IFieldDefinition => {
        if (ks.kErrorMode === true) { return {} as IFieldDefinition; }
        try {
            const format: string = DBImportExport.MapSpFieldType(spField.TypeAsString);
            const fieldDef: IFieldDefinition = {
                Title: spField.Title,
                Type: "string",
                Format: format,
                Description: spField.Description || "",
                Required: spField.Required || false
            } as IFieldDefinition;
            DBImportExport.ApplyFieldDefaults(fieldDef, spField);
            DBImportExport.ApplyFieldChoices(fieldDef, spField, format);
            DBImportExport.ApplyFieldLookup(fieldDef, spField, format);
            DBImportExport.ApplyFieldFormVisibility(fieldDef, spField);
            DBImportExport.ApplyFieldValidation(fieldDef, spField);
            DBImportExport.ApplyFieldTextSettings(fieldDef, spField);
            DBImportExport.ApplyFieldNumberSettings(fieldDef, spField);
            DBImportExport.ApplyFieldMiscSettings(fieldDef, spField);
            return fieldDef;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ConvertSingleSpField");
            return {} as IFieldDefinition;
        }
    };

    private static ApplyFieldDefaults = (fieldDef: IFieldDefinition, spField: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (spField.DefaultValue !== null && spField.DefaultValue !== undefined && spField.DefaultValue !== "") {
                fieldDef.DefaultValue = spField.DefaultValue;
            }
            if (spField.CustomFormatter !== null && spField.CustomFormatter !== undefined && spField.CustomFormatter !== "") {
                fieldDef.CustomFormatter = spField.CustomFormatter;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyFieldDefaults");
        }
    };

    private static ApplyFieldChoices = (fieldDef: IFieldDefinition, spField: any, format: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (format !== "choice" && format !== "multichoice") {
                return;
            }
            let choices: string[] = [];
            if (spField.Choices !== null && spField.Choices !== undefined) {
                if (spField.Choices.results !== null && spField.Choices.results !== undefined) {
                    choices = spField.Choices.results;
                } else if (Array.isArray(spField.Choices)) {
                    choices = spField.Choices;
                }
            }
            if (format === "multichoice") {
                fieldDef.Type = "array";
                fieldDef.Items = { Enum: choices } as IFieldItems;
            } else {
                fieldDef.Enum = choices;
            }
            if (spField.FillInChoice !== null && spField.FillInChoice !== undefined) {
                fieldDef.FillInChoice = spField.FillInChoice;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyFieldChoices");
        }
    };

    private static ApplyFieldLookup = (fieldDef: IFieldDefinition, spField: any, format: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (format !== "lookup") {
                return;
            }
            if (spField.SchemaXml !== null && spField.SchemaXml !== undefined) {
                const listMatch: RegExpMatchArray | null = spField.SchemaXml.match(/List="({[^}]+})"/);
                if (listMatch !== null) {
                    fieldDef.LookupList = listMatch[1];
                }
            }
            if (spField.LookupField !== null && spField.LookupField !== undefined) {
                fieldDef.LookupField = spField.LookupField;
            }
            if (spField.AllowMultipleValues !== null && spField.AllowMultipleValues !== undefined) {
                fieldDef.AllowMultipleValues = spField.AllowMultipleValues;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyFieldLookup");
        }
    };

    private static ApplyFieldFormVisibility = (fieldDef: IFieldDefinition, spField: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (spField.ShowInDisplayForm !== null && spField.ShowInDisplayForm !== undefined) {
                fieldDef.ShowInDisplayForm = spField.ShowInDisplayForm;
            }
            if (spField.ShowInEditForm !== null && spField.ShowInEditForm !== undefined) {
                fieldDef.ShowInEditForm = spField.ShowInEditForm;
            }
            if (spField.ShowInNewForm !== null && spField.ShowInNewForm !== undefined) {
                fieldDef.ShowInNewForm = spField.ShowInNewForm;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyFieldFormVisibility");
        }
    };

    private static ApplyFieldValidation = (fieldDef: IFieldDefinition, spField: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (spField.ValidationFormula !== null && spField.ValidationFormula !== undefined && spField.ValidationFormula !== "") {
                fieldDef.ValidationFormula = spField.ValidationFormula;
            }
            if (spField.ValidationMessage !== null && spField.ValidationMessage !== undefined && spField.ValidationMessage !== "") {
                fieldDef.ValidationMessage = spField.ValidationMessage;
            }
            if (spField.EnforceUniqueValues !== null && spField.EnforceUniqueValues !== undefined) {
                fieldDef.EnforceUniqueValues = spField.EnforceUniqueValues;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyFieldValidation");
        }
    };

    private static ApplyFieldTextSettings = (fieldDef: IFieldDefinition, spField: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (spField.MaxLength !== null && spField.MaxLength !== undefined && spField.MaxLength !== 255) {
                fieldDef.MaxLength = spField.MaxLength;
            }
            if (spField.NumberOfLines !== null && spField.NumberOfLines !== undefined && spField.NumberOfLines !== 6) {
                fieldDef.NumberOfLines = spField.NumberOfLines;
            }
            if (spField.RichText !== null && spField.RichText !== undefined) {
                fieldDef.RichText = spField.RichText;
            }
            if (spField.AppendOnly !== null && spField.AppendOnly !== undefined) {
                fieldDef.AppendOnly = spField.AppendOnly;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyFieldTextSettings");
        }
    };

    private static ApplyFieldNumberSettings = (fieldDef: IFieldDefinition, spField: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (spField.MinimumValue !== null && spField.MinimumValue !== undefined) {
                fieldDef.MinimumValue = spField.MinimumValue;
            }
            if (spField.MaximumValue !== null && spField.MaximumValue !== undefined) {
                fieldDef.MaximumValue = spField.MaximumValue;
            }
            if (spField.ShowAsPercentage !== null && spField.ShowAsPercentage !== undefined) {
                fieldDef.ShowAsPercentage = spField.ShowAsPercentage;
            }
            if (spField.DisplayFormat !== null && spField.DisplayFormat !== undefined) {
                fieldDef.DisplayFormat = spField.DisplayFormat;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyFieldNumberSettings");
        }
    };

    private static ApplyFieldMiscSettings = (fieldDef: IFieldDefinition, spField: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (spField.FriendlyDisplayFormat !== null && spField.FriendlyDisplayFormat !== undefined) {
                fieldDef.FriendlyDisplayFormat = spField.FriendlyDisplayFormat;
            }
            if (spField.SelectionMode !== null && spField.SelectionMode !== undefined && spField.SelectionMode !== 0) {
                fieldDef.SelectionMode = spField.SelectionMode;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyFieldMiscSettings");
        }
    };

    private static ConvertSpViews = (spList: any): IViewDefinition[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            if (spList.Views === null || spList.Views === undefined) {
                return [];
            }
            const views: any[] = spList.Views.results || spList.Views;
            if (!Array.isArray(views)) {
                return [];
            }
            const convertView = (spView: any): IViewDefinition => {
                return DBImportExport.ConvertSingleSpView(spView);
            };
            const viewDefs: IViewDefinition[] = views.map(convertView);
            return viewDefs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ConvertSpViews");
            return [];
        }
    };

    private static ConvertSingleSpView = (spView: any): IViewDefinition => {
        if (ks.kErrorMode === true) { return {} as IViewDefinition; }
        try {
            let viewFields: string[] = [];
            if (spView.ViewFields !== null && spView.ViewFields !== undefined) {
                if (spView.ViewFields.Items !== null && spView.ViewFields.Items !== undefined) {
                    viewFields = spView.ViewFields.Items.results || spView.ViewFields.Items;
                } else if (spView.ViewFields.SchemaXml !== null && spView.ViewFields.SchemaXml !== undefined) {
                    viewFields = DBImportExport.ParseViewFieldsXml(spView.ViewFields.SchemaXml);
                }
            }
            let aggregations: IAggregation[] = [];
            if (spView.Aggregations !== null && spView.Aggregations !== undefined && spView.Aggregations !== "") {
                aggregations = DBImportExport.ParseAggregationsXml(spView.Aggregations);
            }
            const viewDef: IViewDefinition = {
                Id: spView.Id || "",
                Title: spView.Title || "",
                IsDefault: spView.DefaultView || false,
                ViewType: spView.ViewType || "",
                ViewFields: viewFields,
                ViewQuery: spView.ViewQuery || "",
                RowLimit: spView.RowLimit || 30,
                Paged: spView.Paged || false,
                Hidden: spView.Hidden || false,
                Scope: spView.Scope || 0,
                Aggregations: aggregations,
                CustomFormatter: spView.CustomFormatter || ""
            } as IViewDefinition;
            return viewDef;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ConvertSingleSpView");
            return {} as IViewDefinition;
        }
    };

    private static ParseViewFieldsXml = (xml: string): string[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const fields: string[] = [];
            const regex: RegExp = /FieldRef\s+Name="([^"]+)"/g;
            let match: RegExpExecArray | null = regex.exec(xml);
            while (match !== null) {
                fields.push(match[1]);
                match = regex.exec(xml);
            }
            return fields;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ParseViewFieldsXml");
            return [];
        }
    };

    public static ParseAggregationsXml = (xml: string): IAggregation[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const aggregations: IAggregation[] = [];
            const regex: RegExp = /FieldRef\s+Name="([^"]+)"\s+Type="([^"]+)"/g;
            let match: RegExpExecArray | null = regex.exec(xml);
            while (match !== null) {
                const agg: IAggregation = {
                    FieldName: match[1],
                    Function: match[2]
                };
                aggregations.push(agg);
                match = regex.exec(xml);
            }
            return aggregations;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ParseAggregationsXml");
            return [];
        }
    };

    private static ParseListSettings = (spList: any): IListSettings | null => {
        if (ks.kErrorMode === true) { return null; }
        try {
            const settings: IListSettings = {};
            if (spList.Description !== null && spList.Description !== undefined && spList.Description !== "") {
                settings.Description = spList.Description;
            }
            DBImportExport.ApplyBooleanSetting(settings, "EnableAttachments", spList.EnableAttachments);
            DBImportExport.ApplyBooleanSetting(settings, "EnableVersioning", spList.EnableVersioning);
            DBImportExport.ApplyBooleanSetting(settings, "EnableMinorVersions", spList.EnableMinorVersions);
            DBImportExport.ApplyNumericSetting(settings, "MajorVersionLimit", spList.MajorVersionLimit);
            DBImportExport.ApplyNumericSetting(settings, "MajorWithMinorVersionsLimit", spList.MajorWithMinorVersionsLimit);
            DBImportExport.ApplyNumericSetting(settings, "DraftVersionVisibility", spList.DraftVersionVisibility);
            DBImportExport.ApplyBooleanSetting(settings, "ForceCheckout", spList.ForceCheckout);
            DBImportExport.ApplyBooleanSetting(settings, "EnableModeration", spList.EnableModeration);
            DBImportExport.ApplyBooleanSetting(settings, "NoCrawl", spList.NoCrawl);
            const hasKeys: boolean = Object.keys(settings).length > 0;
            return hasKeys ? settings : null;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ParseListSettings");
            return null;
        }
    };

    private static ApplyBooleanSetting = (settings: IListSettings, key: string, value: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (value !== null && value !== undefined) {
                settings[key] = value;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyBooleanSetting");
        }
    };

    private static ApplyNumericSetting = (settings: IListSettings, key: string, value: any): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (value !== null && value !== undefined) {
                settings[key] = value;
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyNumericSetting");
        }
    };

    private static ResolveLookupReferences = (newLists: IListDefinition[], allSpLists: any[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const listIdToTitle: Record<string, string> = {};
            const buildIdMap = (spList: any): void => {
                if (spList.Id !== null && spList.Id !== undefined) {
                    const idKey: string = "{" + spList.Id + "}";
                    listIdToTitle[idKey.toLowerCase()] = spList.Title;
                }
            };
            allSpLists.forEach(buildIdMap);
            const resolveListLookups = (list: IListDefinition): void => {
                DBImportExport.ResolveListFieldLookups(list, listIdToTitle);
            };
            newLists.forEach(resolveListLookups);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ResolveLookupReferences");
        }
    };

    private static ResolveListFieldLookups = (list: IListDefinition, idToTitle: Record<string, string>): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fieldKeys: string[] = Object.keys(list.Properties);
            const resolveField = (key: string): void => {
                const field: IFieldDefinition = list.Properties[key];
                if (field.LookupList !== null && field.LookupList !== undefined && field.LookupList !== "") {
                    const lookupKey: string = field.LookupList.toLowerCase();
                    const title: string | undefined = idToTitle[lookupKey];
                    if (title !== undefined) {
                        field.LookupList = title;
                    }
                }
            };
            fieldKeys.forEach(resolveField);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ResolveListFieldLookups");
        }
    };

    public static OpenRestApiDataModal = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const selectedId: string = DBState.SelectedListId;
            if (selectedId === null || selectedId === undefined || selectedId === "") {
                DBModalManager.ShowToast("Please select a list first", "error");
                return;
            }
            const list: IListDefinition | undefined = DBImportExport.FindSelectedList(selectedId);
            if (list === undefined) {
                DBModalManager.ShowToast("Selected list not found", "error");
                return;
            }
            const fieldKeys: string[] = Object.keys(list.Properties);
            if (fieldKeys.length === 0) {
                DBModalManager.ShowToast("List has no fields defined", "error");
                return;
            }
            DBImportExport.BuildDataImportPanel(list, fieldKeys);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.OpenRestApiDataModal");
        }
    };

    private static FindSelectedList = (selectedId: string): IListDefinition | undefined => {
        if (ks.kErrorMode === true) { return undefined; }
        try {
            const matchList = (list: IListDefinition): boolean => {
                return list.Id === selectedId;
            };
            const found: IListDefinition | undefined = DBState.Lists.find(matchList);
            return found;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.FindSelectedList");
            return undefined;
        }
    };

    private static BuildDataImportPanel = (list: IListDefinition, fieldKeys: string[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const selectFields: string = fieldKeys.join(",");
            const restUrl: string = "/_api/web/lists/getbytitle('" +
                DBStringUtils.EscapeHtml(list.Title) + "')/items?$select=" +
                selectFields + "&$top=5000";
            const panelHtml: string = DBImportExport.BuildDataImportHtml(restUrl);
            const container: HTMLElement = document.getElementById("dataGridContainer") as HTMLElement;
            const existingPanel: HTMLElement | null = document.getElementById("restApiDataPanel");
            if (existingPanel !== null) {
                existingPanel.remove();
            }
            const panelDiv: HTMLDivElement = document.createElement("div");
            panelDiv.id = "restApiDataPanel";
            panelDiv.innerHTML = panelHtml;
            container.parentNode!.insertBefore(panelDiv, container);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.BuildDataImportPanel");
        }
    };

    private static BuildDataImportHtml = (restUrl: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const html: string = '<div class="rest-api-data-panel">' +
                '<h3>Import Data from REST API</h3>' +
                '<p>REST API URL for items:</p>' +
                '<textarea id="restApiDataUrl" readonly rows="3" class="rest-api-url">' +
                DBStringUtils.EscapeHtml(restUrl) + '</textarea>' +
                '<p>Paste the JSON response below:</p>' +
                '<textarea id="restApiDataInput" rows="10" placeholder="Paste REST API JSON response here..."></textarea>' +
                '<div class="rest-api-data-buttons">' +
                '<button onclick="DBImportExport.ProcessRestApiData()" class="btn-primary">Import Data</button>' +
                '<button onclick="DBImportExport.CloseDataImportPanel()" class="btn-secondary">Cancel</button>' +
                '</div></div>';
            return html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.BuildDataImportHtml");
            return "";
        }
    };

    public static CloseDataImportPanel = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const panel: HTMLElement | null = document.getElementById("restApiDataPanel");
            if (panel !== null) {
                panel.remove();
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.CloseDataImportPanel");
        }
    };

    public static ProcessRestApiData = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const jsonInput: HTMLTextAreaElement = document.getElementById("restApiDataInput") as HTMLTextAreaElement;
            const jsonText: string = jsonInput.value.trim();
            if (jsonText === "") {
                DBModalManager.ShowToast("Please paste the REST API JSON response", "error");
                return;
            }
            const selectedId: string = DBState.SelectedListId;
            const list: IListDefinition | undefined = DBImportExport.FindSelectedList(selectedId);
            if (list === undefined) {
                DBModalManager.ShowToast("Selected list not found", "error");
                return;
            }
            const data: any = JSON.parse(jsonText);
            const items: any[] = DBImportExport.ExtractDataItems(data);
            if (items.length === 0) {
                DBModalManager.ShowToast("No items found in response", "error");
                return;
            }
            DBImportExport.ApplyDataItems(list, items);
        } catch (e) {
            DBModalManager.ShowToast("Invalid JSON format", "error");
            ks.kGlobalErrorHandler(e, "DBImportExport.ProcessRestApiData");
        }
    };

    private static ExtractDataItems = (data: any): any[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            if (data.d !== null && data.d !== undefined) {
                if (data.d.results !== null && data.d.results !== undefined) {
                    return data.d.results;
                }
                return data.d;
            }
            if (data.value !== null && data.value !== undefined) {
                return data.value;
            }
            if (Array.isArray(data)) {
                return data;
            }
            return [];
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ExtractDataItems");
            return [];
        }
    };

    private static ApplyDataItems = (list: IListDefinition, items: any[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const fieldKeys: string[] = Object.keys(list.Properties);
            const mapItem = (item: any): IDataRow => {
                return DBImportExport.MapSingleDataItem(item, fieldKeys);
            };
            const dataRows: IDataRow[] = items.map(mapItem);
            const appendRow = (row: IDataRow): void => {
                list.Data.push(row);
            };
            dataRows.forEach(appendRow);
            DBState.SaveState();
            DBDataGrid.RenderDataGrid(list);
            DBImportExport.CloseDataImportPanel();
            DBModalManager.ShowToast("Imported " + dataRows.length + " item(s)", "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.ApplyDataItems");
        }
    };

    private static MapSingleDataItem = (item: any, fieldKeys: string[]): IDataRow => {
        if (ks.kErrorMode === true) { return {}; }
        try {
            const row: IDataRow = {};
            const mapField = (key: string): void => {
                if (item[key] !== null && item[key] !== undefined) {
                    row[key] = item[key];
                }
            };
            fieldKeys.forEach(mapField);
            return row;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBImportExport.MapSingleDataItem");
            return {};
        }
    };
}
